import React from 'react'
import BaseElevationLayer from '@arcgis/core/layers/BaseElevationLayer'
import ElevationLayer from '@arcgis/core/layers/ElevationLayer'
import Graphic from '@arcgis/core/Graphic'
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol'
import Point from '@arcgis/core/geometry/Point'
import Polygon from '@arcgis/core/geometry/Polygon'
import Polyline from '@arcgis/core/geometry/Polyline'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol'
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol'
import { FEATURE_TYPES, MAP_MODES } from './constants'
import { svgToDataURI, getColor } from './utils'
import WaypointIcon from '../assets/svg/WaypointIcon'

export const getLabelGraphic = (label) => {
	const textSymbol = {
		type: 'text', // autocasts as new TextSymbol()
		color: 'white',
		// haloColor: 'white',
		// haloSize: '1px',
		text: label.title,
		verticalAlignment: 'bottom',
		horizontalAlignment: 'center',
		font: {
			// autocasts as new Font()
			size: 14,
			family: 'Inter', // right now, it's probably still using default font.
			weight: 'regular',
		},
	}

	const textGraphic = new Graphic(label.location, textSymbol, { id: label.id })

	return textGraphic
}

const getAreaGraphic = (feature) => {
	const polygon = new Polygon({
		rings: [feature.geometry.coordinates[0]], // maybe todo: .map(i => [i[0],i[1]])
		hasZ: false,
	})

	/*
      #TODO: Currently using transparentFill, but maybe later use the feature's opacity value itself.
      -- KD - August 28, 2020
    */
	const graphic = new Graphic({
		attributes: feature,
		geometry: polygon,
		symbol: {
			type: 'simple-fill', // autocasts as new PolygonSymbol3D()
			color: feature.transparentFill ? 'transparent' : getColor(feature, 0.2),
			outline: {
				color: getColor(feature),
				// style: 'short-dot',
				width: feature.properties.strokeWidth,
			},
		},
		popupTemplate: graphicsPopupTemplate,
	})

	return graphic
}

const getLineGraphic = (feature) => {
	const polyline = new Polyline({
		paths: [feature.geometry.coordinates],
		hasZ: false,
	})

	const graphic = new Graphic({
		attributes: feature,
		geometry: polyline,
		symbol: {
			type: 'simple-line', // autocasts as new LineSymbol()
			cap: 'round',
			join: 'round',
			color: getColor(feature),
			width: feature.properties.strokeWidth,
		},
		popupTemplate: graphicsPopupTemplate,
	})

	return graphic
}

const getTracksGraphic = (feature) => {
	const polyline = new Polyline({
		paths: feature.geometry.coordinates,
		hasZ: false,
	})
	// Create a graphic and add the geometry and symbol to it
	const graphic = new Graphic({
		attributes: feature,
		geometry: polyline,
		symbol: {
			type: 'simple-line', // autocasts as new LineSymbol()
			cap: 'round',
			join: 'round',
			color: getColor(feature),
			width: feature.properties.strokeWidth,
		},
		popupTemplate: graphicsPopupTemplate,
	})

	return graphic
}

const getWaypointGraphic = (feature) => {
	let coords = feature.geometry.coordinates
	const point = new Point({
		longitude: coords[0],
		latitude: coords[1],
		spatialReference: {
			wkid: 3857, // This is important for when editing waypoint location.
		},
	})

	let icon = feature.properties.icon
	const markerSymbol = new PictureMarkerSymbol({
		url: svgToDataURI(<WaypointIcon icon={icon} color={getColor(feature)} />),
		width: '34px',
		height: '42px',
		yoffset: '21px',
	})

	const graphic = new Graphic({
		attributes: feature,
		geometry: point,
		symbol: markerSymbol,
	})

	return graphic
}

const getAreaSymbol = (feature) => {
	return new SimpleFillSymbol({
		color: getColor(feature, 0.2),
		outline: {
			color: getColor(feature),
			// style: 'short-dot',
			width: feature.properties.strokeWidth,
		},
	})
}

const getLineSymbol = (feature) => {
	return new SimpleLineSymbol({
		cap: 'round',
		join: 'round',
		color: getColor(feature),
		width: feature.properties.strokeWidth,
	})
}

const getTracksSymbol = (feature) => {
	return new SimpleLineSymbol({
		cap: 'round',
		join: 'round',
		color: getColor(feature),
		width: feature.properties.strokeWidth,
	})
}

const getWaypointSymbol = (feature) => {
	const icon = feature.properties.icon
	const markerSymbol = new PictureMarkerSymbol({
		url: svgToDataURI(<WaypointIcon icon={icon} color={getColor(feature)} />),
		width: '34px',
		height: '42px',
		yoffset: '21px',
	})

	return markerSymbol
}

export const getActiveViewRef = (model) => {
	if (model.map.mapMode == MAP_MODES.TwoD) {
		return model.map.mapViewRef
	} else if (model.map.mapMode == MAP_MODES.ThreeD) {
		return model.map.sceneViewRef
	} else {
		return null
	}
}

export const getFeatureGraphic = (feature) => {
	switch (feature.properties.ghType) {
		case FEATURE_TYPES.Point:
			return getWaypointGraphic(feature)
		case FEATURE_TYPES.Tracks:
			return getTracksGraphic(feature)
		case FEATURE_TYPES.Polyline:
			return getLineGraphic(feature)
		case FEATURE_TYPES.Polygon:
			return getAreaGraphic(feature)
		default:
			return null
	}
}

export const getFeatureSymbol = (feature) => {
	switch (feature.properties.ghType) {
		case FEATURE_TYPES.Point:
			return getWaypointSymbol(feature)
		case FEATURE_TYPES.Tracks:
			return getTracksSymbol(feature)
		case FEATURE_TYPES.Polyline:
			return getLineSymbol(feature)
		case FEATURE_TYPES.Polygon:
			return getAreaSymbol(feature)
		default:
			return null
	}
}

const graphicsPopupTemplate = {
	title: '',
	content: '',
}

export const getGhostWaypointGraphic = (feature) => {
	let coords = feature.geometry.coordinates

	const point = new Point({
		longitude: coords[0],
		latitude: coords[1],
		spatialReference: {
			wkid: 3857, // This is important for when editing waypoint location.
		},
	})

	const graphic = new Graphic({
		attributes: feature,
		geometry: point,
		symbol: {
			type: 'simple-marker',
			style: 'circle',
			size: 8,
			color: [255, 255, 255],
			outline: {
				color: [255, 255, 255],
				width: 1,
			},
		},
		popupTemplate: graphicsPopupTemplate,
	})

	return graphic
}

export const getTerrain = (exaggeration) => {
	const ExaggeratedElevationLayer = BaseElevationLayer.createSubclass({
		// Add an exaggeration property whose value will be used
		// to multiply the elevations at each tile by a specified
		// factor. In this case terrain will render 100x the actual elevation.

		properties: {
			exaggeration,
		},

		// The load() method is called when the layer is added to the map
		// prior to it being rendered in the view.

		load: function () {
			this._elevation = new ElevationLayer({
				url: '//elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
			})

			// wait for the elevation layer to load before resolving load()
			this.addResolvingPromise(this._elevation.load())
		},

		// Fetches the tile(s) visible in the view
		fetchTile: function (level, row, col, options) {
			// calls fetchTile() on the elevationlayer for the tiles
			// visible in the view
			return this._elevation.fetchTile(level, row, col, options).then(
				function (data) {
					// `data` is an object that contains the
					// the width of the tile in pixels,
					// the height of the tile in pixels,
					// and the values of each pixel
					for (let vi = 0; vi < data.values.length; vi++) {
						// each value represents an elevation sample for the
						// given pixel position in the tile. Multiply this
						// by the exaggeration value
						data.values[vi] *= this.exaggeration
					}

					return data
				}.bind(this)
			)
		},
	})

	return new ExaggeratedElevationLayer()
}
